import { observer } from 'mobx-react';
import * as React from 'react';
import ReactHtmlParser from 'react-html-parser';
import ContentEmbed from '../../../components/ContentEmbed';
import { nodeDecoder } from '../../../components/ContentEmbed/Types';
import { AnnouncementResource } from '../../Decoders/Types';
import * as style from '../../style.module.css';
import { embeddable, whenAnnouncementEmbeddableNode } from './Types';

interface Props {
  announcement: AnnouncementResource;
  footerHTML: string;
}

// Type definitions for ReactHtmlParser nodes
// This matches what ReactHtmlParser actually provides, not the HtmlNode type from SegmentStore
interface HtmlParserNode {
  type: string;
  name?: string;
  children?: HtmlParserNode[];
  attribs?: Record<string, string>;
  data?: string;
}

// Validate URL to prevent XSS attacks
const isValidUrl = (url: string): boolean => {
  try {
    const parsed = new URL(url);
    // Only allow http and https protocols
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

// Helper function to extract all text content from a node tree
const extractTextContent = (node: HtmlParserNode): string => {
  if (node.type === 'text') {
    return node.data || '';
  }
  if (node.children) {
    return node.children.map(extractTextContent).join('');
  }
  return '';
};

// Transform for description HTML - only handles links, not embeddables
const descriptionTransform =
  () =>
  (htmlNode: HtmlParserNode, key: number): React.ReactNode => {
    // Handle regular anchor tags
    if (htmlNode.type === 'tag' && htmlNode.name === 'a') {
      const href = htmlNode.attribs?.href || '';

      // Validate URL before using it
      if (!isValidUrl(href)) {
        const children = htmlNode.children || [];
        const textChild = children.find((child: HtmlParserNode) => child.type === 'text');
        const linkText = textChild?.data || '';
        // If URL is invalid, just return the text without a link
        return <span key={key}>{linkText}</span>;
      }

      // Extract all text content from the link, including nested elements
      const linkText = extractTextContent(htmlNode);
      const trimmedLinkText = linkText.toLowerCase().trim();

      // If link text is just "link", add an aria-label with a descriptive label
      if (trimmedLinkText === 'link') {
        let ariaLabel = 'External link';

        // Try to create a more descriptive label based on the URL hostname
        try {
          const url = new URL(href);
          const hostname = url.hostname.toLowerCase();

          if (hostname === 'linkedin.com' || hostname.endsWith('.linkedin.com')) {
            ariaLabel = 'Add to LinkedIn profile';
          } else if (hostname === 'sendgrid.net' || hostname.endsWith('.sendgrid.net')) {
            ariaLabel = 'Join the ExecOnline LinkedIn Alumni group';
          }
        } catch {
          // If URL parsing fails, use default label
          ariaLabel = 'External link';
        }

        return (
          <a key={key} href={href} aria-label={ariaLabel} target="_blank" rel="noopener noreferrer">
            {linkText}
          </a>
        );
      }

      // For links with visible text, let ReactHtmlParser handle them normally
      // by returning undefined so it falls through to default processing
    }

    // Don't handle embeddables in description - return undefined to use default processing
    return undefined;
  };

// Transform for footer HTML - handles both links and embeddables
const footerTransform =
  (announcement: AnnouncementResource) =>
  (htmlNode: HtmlParserNode, key: number): React.ReactNode => {
    // Handle regular anchor tags
    if (htmlNode.type === 'tag' && htmlNode.name === 'a') {
      const href = htmlNode.attribs?.href || '';

      // Validate URL before using it
      if (!isValidUrl(href)) {
        const children = htmlNode.children || [];
        const textChild = children.find((child: HtmlParserNode) => child.type === 'text');
        const linkText = textChild?.data || '';
        // If URL is invalid, just return the text without a link
        return <span key={key}>{linkText}</span>;
      }

      // Extract all text content from the link, including nested elements
      const linkText = extractTextContent(htmlNode);
      const trimmedLinkText = linkText.toLowerCase().trim();

      // If link text is just "link", add an aria-label with a descriptive label
      if (trimmedLinkText === 'link') {
        let ariaLabel = 'External link';

        // Try to create a more descriptive label based on the URL hostname
        try {
          const url = new URL(href);
          const hostname = url.hostname.toLowerCase();

          if (hostname === 'linkedin.com' || hostname.endsWith('.linkedin.com')) {
            ariaLabel = 'Add to LinkedIn profile';
          } else if (hostname === 'sendgrid.net' || hostname.endsWith('.sendgrid.net')) {
            ariaLabel = 'Join the ExecOnline LinkedIn Alumni group';
          }
        } catch {
          // If URL parsing fails, use default label
          ariaLabel = 'External link';
        }

        return (
          <a key={key} href={href} aria-label={ariaLabel} target="_blank" rel="noopener noreferrer">
            {linkText}
          </a>
        );
      }

      // For links with visible text, let ReactHtmlParser handle them normally
      // by returning undefined so it falls through to default processing
    }

    // Handle embeddable content nodes (only in footer)
    return nodeDecoder('announcement')
      .decodeAny(htmlNode)
      .andThen(whenAnnouncementEmbeddableNode)
      .andThen((node) => embeddable(node, announcement))
      .cata({
        Err: () => undefined,
        Ok: (embeddable) => <ContentEmbed key={key} embeddable={embeddable} />,
      });
  };

const DigitalCertificateContent: React.FC<Props> = ({ announcement, footerHTML }) => {
  return (
    <div data-test-announcement-content className={style.content} tabIndex={0}>
      <div className={style.description}>
        {announcement.payload.descriptionHtml
          .map((html) => ReactHtmlParser(html, { transform: descriptionTransform() }))
          .getOrElseValue(<span>{announcement.payload.description}</span>)}
      </div>
      {ReactHtmlParser(footerHTML, { transform: footerTransform(announcement) })}
    </div>
  );
};

export default observer(DigitalCertificateContent);
